import React from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import type { SilaeVariable } from "@/types/silae";
import type { AdvancedVariableParams, SilaeFormData } from "@/types/silae";
import { VariableInput } from "./VariableInput";
import { SilaeDatePicker } from "./SilaeDatePicker";
import { useTranslation } from "react-i18next";

/**
 * Interface pour le composant BaseParametersRenderer
 */
export interface BaseParametersRendererProps {
  formik: FormikProps<SilaeFormData>;
  selectedVariable: SilaeVariable | null;
  fieldPrefix: string;
}

/**
 * Composant pour rendre les paramètres dynamiques d'une variable dans le contexte de source de base
 */
export const BaseParametersRenderer: React.FC<BaseParametersRendererProps> = ({
  formik,
  selectedVariable,
  fieldPrefix,
}) => {
  const { t } = useTranslation();

  if (!selectedVariable?.parameters) return null;

  const parametersFieldName = fieldPrefix;
  const parameters = (getIn(formik.values, parametersFieldName) ||
    []) as Array<AdvancedVariableParams>;

  const renderParameterInput = (
    paramFromVariable: NonNullable<SilaeVariable["parameters"]>[0],
    index: number,
  ) => {
    // Ignorer les paramètres avec valeurs fixes
    if (paramFromVariable.isFixed) {
      return null;
    }
    // Ne pas afficher les variables de sortie (déclarées avant l'appel, pas saisies par l'utilisateur)
    if (paramFromVariable.isOutputParam) {
      return null;
    }

    // Récupérer le paramètre complet depuis Formik (qui contient toutes les métadonnées + valeurs)
    const currentParameter = parameters.find(
      (p) => p.id === paramFromVariable.id,
    );

    if (!currentParameter) {
      return null;
    }

    // Trouver l'index réel du paramètre dans les paramètres
    const parameterIndex = parameters.findIndex(
      (p) => p.id === paramFromVariable.id,
    );
    const actualIndex = parameterIndex >= 0 ? parameterIndex : index;

    const valueFieldName = `${parametersFieldName}[${actualIndex}].value`;
    const shouldIncludeBulletinCourantFieldName = `${parametersFieldName}[${actualIndex}].shouldIncludeBulletinCourant`;

    const updateParameter = (
      updates: Partial<{
        value: string;
        shouldIncludeBulletinCourant: boolean;
      }>,
    ) => {
      const newParameters = [...parameters];
      const existingIndex = newParameters.findIndex(
        (p) => p.id === currentParameter.id,
      );

      if (existingIndex >= 0) {
        newParameters[existingIndex] = {
          ...newParameters[existingIndex],
          ...updates,
        };
      } else {
        newParameters.push({
          ...currentParameter,
          ...updates,
        });
      }

      formik.setFieldValue(parametersFieldName, newParameters);
    };

    return (
      <div
        key={currentParameter.id}
        className="space-y-2 p-3 border rounded-md bg-slate-50 dark:bg-slate-900/20"
      >
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">
            {currentParameter.label || paramFromVariable.label}
            {currentParameter.isRequired && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </Label>
        </div>

        {/* Helper text */}
        {currentParameter.helper && (
          <p className="text-xs text-muted-foreground">
            {currentParameter.helper}
          </p>
        )}

        {/* Input principal selon le type */}
        {(() => {
          const paramType = currentParameter.type || "";
          switch (paramType) {
            case "selection": {
              if (!Array.isArray(currentParameter.options)) {
                return null;
              }
              return (
                <div>
                  <Select
                    value={currentParameter.value || ""}
                    onValueChange={(val) => {
                      updateParameter({ value: val });
                      formik.setFieldValue(valueFieldName, val);
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue
                        placeholder={
                          currentParameter.placeholder ||
                          t("baseParameters.select.placeholder")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {currentParameter.options?.map((opt) => (
                        <SelectItem
                          key={opt.code}
                          value={opt.code}
                          className="text-xs"
                        >
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            case "dynamicDate": {
              return (
                <SilaeDatePicker
                  value={currentParameter.value || ""}
                  onChange={(val) => {
                    updateParameter({ value: val });
                    formik.setFieldValue(valueFieldName, val);
                  }}
                  placeholder={currentParameter.placeholder}
                  label={currentParameter.label}
                />
              );
            }
            default: {
              return (
                <VariableInput
                  formik={formik}
                  fieldName={valueFieldName}
                  variableType={currentParameter.type}
                  placeholder={currentParameter.placeholder}
                />
              );
            }
          }
        })()}

        {/* Option includeBulletinCourant - affichée si le paramètre supporte cette option */}
        {currentParameter.type === "selection" &&
          paramFromVariable.includeBulletinCourant && (
            <div className="flex items-center gap-2 mt-2">
              <Switch
                checked={currentParameter.shouldIncludeBulletinCourant || false}
                onCheckedChange={(checked) => {
                  updateParameter({ shouldIncludeBulletinCourant: checked });
                  formik.setFieldValue(
                    shouldIncludeBulletinCourantFieldName,
                    checked,
                  );
                }}
              />
              <Label className="text-xs text-muted-foreground">
                {t("baseParameters.includeBulletinsCourants")}
              </Label>
            </div>
          )}

        {/* Note si présente */}
        {currentParameter.note && (
          <p className="text-xs text-blue-600 dark:text-blue-400">
            ℹ️ {currentParameter.note}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">
        {t("baseParameters.parametersOf", {
          variable: selectedVariable.label || selectedVariable.code,
        })}
      </Label>
      {selectedVariable.parameters.map((parameter, index) =>
        renderParameterInput(parameter, index),
      )}
    </div>
  );
};
